import { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { useApi } from './lib/useApi';
import { useRouteSeo, siteJsonLd } from './lib/useSeo';
import { metaForPath } from './lib/seo';
import { Shell } from './components/Shell';
import { Spinner } from './components/ui';
import type { PublicConfig } from '@shared/types';

import Landing from './pages/Landing';
import StaffLogin from './pages/StaffLogin';
import TenantLogin from './pages/TenantLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Setup from './pages/Setup';
import NotFound from './pages/NotFound';

import PublicShell from './pages/public/PublicShell';
import Pricing from './pages/public/Pricing';
import Tour from './pages/public/Tour';
import Privacy from './pages/public/legal/Privacy';
import Terms from './pages/public/legal/Terms';
import DataProcessing from './pages/public/legal/DataProcessing';
import Refunds from './pages/public/legal/Refunds';
import Cookies from './pages/public/legal/Cookies';

import Dashboard from './pages/admin/Dashboard';
import Properties from './pages/admin/Properties';
import PropertyDetail from './pages/admin/PropertyDetail';
import Rooms from './pages/admin/Rooms';
import Tenants from './pages/admin/Tenants';
import TenantDetail from './pages/admin/TenantDetail';
import Payments from './pages/admin/Payments';
import Charges from './pages/admin/Charges';
import Reminders from './pages/admin/Reminders';
import Bills from './pages/admin/Bills';
import Documents from './pages/admin/Documents';
import Agreements from './pages/admin/Agreements';
import Messages from './pages/admin/Messages';
import Maintenance from './pages/admin/Maintenance';
import SettingsPage from './pages/admin/Settings';
import Help from './pages/admin/Help';

import PortalShell from './pages/portal/PortalShell';
import PortalHome from './pages/portal/PortalHome';
import PortalPayments from './pages/portal/PortalPayments';
import PortalDocuments from './pages/portal/PortalDocuments';
import PortalProperty from './pages/portal/PortalProperty';
import PortalUpdates from './pages/portal/PortalUpdates';
import PortalMaintenance from './pages/portal/PortalMaintenance';
import PortalProfile from './pages/portal/PortalProfile';
import PortalMessages from './pages/portal/PortalMessages';
import PortalAgreement from './pages/portal/PortalAgreement';

/*
 * The operator console. Its files live in pages/platform/ rather than
 * pages/admin/, because pages/admin/ is the *manager* app — a naming wart
 * that predates this and would be a worse thing to change than to explain.
 */
import PlatformLogin from './pages/platform/PlatformLogin';
import PlatformShell from './pages/platform/PlatformShell';
import Organisations from './pages/platform/Organisations';
import Inbox from './pages/platform/Inbox';
import Operators from './pages/platform/Operators';
import OrganisationDetail from './pages/platform/OrganisationDetail';
import PlatformEmail from './pages/platform/Email';
import PlatformLeads from './pages/platform/Leads';
import PlatformTemplates from './pages/platform/Templates';
import PlatformActivity from './pages/platform/Activity';
import PlatformSettings from './pages/platform/PlatformSettings';

/**
 * The one place that writes the document head.
 *
 * Route-driven rather than per-page: one component owning `<head>` means two
 * screens can never each set a title and leave the answer to mount order.
 * Anything not declared in PUBLIC_ROUTES is a signed-in screen or a mistyped
 * URL, and gets a noindex — see useRouteSeo.
 *
 * The pricing is fetched only on the two routes whose structured data quotes
 * a price. A figure in JSON-LD that disagrees with the page is worse than no
 * figure at all, so it comes from the same endpoint the pricing page renders
 * from rather than being written down twice.
 */
function SeoHead() {
  const { pathname } = useLocation();
  const quotesAPrice = pathname === '/' || pathname === '/pricing';

  const { data: pub } = useApi<PublicConfig>(quotesAPrice ? '/public/config' : null);

  const jsonLd = useMemo(() => {
    if (!metaForPath(pathname)) return undefined;

    const priced = (pub?.pricing.plans ?? [])
      .map((plan) => plan.price_pence)
      .filter((pence): pence is number => typeof pence === 'number' && pence > 0);

    return siteJsonLd(priced.length
      ? {
        amount: (Math.min(...priced) / 100).toFixed(2),
        currency: pub?.pricing.currency ?? 'GBP',
      }
      : undefined);
  }, [pathname, pub]);

  useRouteSeo(pathname, jsonLd);
  return null;
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <Spinner className="h-8 w-8" />
    </div>
  );
}

export default function App() {
  const { session, loading } = useAuth();

  if (loading) return <FullPageSpinner />;

  const isStaff = session?.kind === 'staff';
  const isTenant = session?.kind === 'tenant';
  const isAdmin = session?.kind === 'admin';

  return (
    <>
      <SeoHead />
      <Routes>
      {/* Public */}
      <Route
        path="/"
        element={
          isStaff ? <Navigate to="/app" replace />
            : isTenant ? <Navigate to="/portal" replace />
            : isAdmin ? <Navigate to="/admin" replace />
            : <Landing />
        }
      />
      <Route path="/login" element={isStaff ? <Navigate to="/app" replace /> : <StaffLogin />} />
      <Route path="/tenant-login" element={isTenant ? <Navigate to="/portal" replace /> : <TenantLogin />} />
      {/*
        Reachable while signed in as well. Somebody who is already in the app
        may still be resetting the password on a second account, and bouncing
        them to /app from a link that arrived by email is the kind of dead end
        that produces a support message.
      */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/*
        Outside the console shell on purpose — the whole point is that you
        cannot sign in yet. A different endpoint from the staff one, because
        the backend refuses to spend a console token at a staff route.
      */}
      <Route
        path="/admin/reset-password"
        element={(
          <ResetPassword
            endpoint="/admin/reset-password"
            signInPath="/admin/login"
            forgotPath={null}
            what="your PROVIA console password"
          />
        )}
      />
      <Route path="/setup" element={<Setup />} />

      {/*
        Public pages. Outside the auth check on purpose — a privacy policy that
        only renders for signed-in users is no use to the tenant or the
        regulator who came looking for it.
      */}
      <Route element={<PublicShell />}>
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/tour" element={<Tour />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/data-processing" element={<DataProcessing />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/cookies" element={<Cookies />} />
      </Route>

      {/* Manager app */}
      <Route path="/app" element={isStaff ? <Shell /> : <Navigate to="/login" replace />}>
        <Route index element={<Dashboard />} />
        <Route path="properties" element={<Properties />} />
        <Route path="properties/:id" element={<PropertyDetail />} />
        <Route path="rooms" element={<Rooms />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="tenants/:id" element={<TenantDetail />} />
        <Route path="payments" element={<Payments />} />
        <Route path="charges" element={<Charges />} />
        <Route path="reminders" element={<Reminders />} />
        <Route path="bills" element={<Bills />} />
        <Route path="documents" element={<Documents />} />
        <Route path="agreements" element={<Agreements />} />
        <Route path="messages" element={<Messages />} />
        <Route path="maintenance" element={<Maintenance />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="help" element={<Help />} />
      </Route>

      {/*
        The operator console.

        Not linked from anywhere in the customer app, and gated server-side on
        every route — a session of the wrong kind gets a 403 from the API, so
        the routing below is convenience rather than security.
      */}
      <Route
        path="/admin/login"
        element={isAdmin ? <Navigate to="/admin" replace /> : <PlatformLogin />}
      />
      <Route
        path="/admin"
        element={isAdmin ? <PlatformShell /> : <Navigate to="/admin/login" replace />}
      >
        <Route index element={<Organisations />} />
        <Route path="organisations/:id" element={<OrganisationDetail />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="operators" element={<Operators />} />
        <Route path="leads" element={<PlatformLeads />} />
        <Route path="email" element={<PlatformEmail />} />
        <Route path="templates" element={<PlatformTemplates />} />
        <Route path="activity" element={<PlatformActivity />} />
        <Route path="settings" element={<PlatformSettings />} />
      </Route>

      {/* Tenant portal */}
      <Route path="/portal" element={isTenant ? <PortalShell /> : <Navigate to="/tenant-login" replace />}>
        <Route index element={<PortalHome />} />
        <Route path="payments" element={<PortalPayments />} />
        <Route path="documents" element={<PortalDocuments />} />
        <Route path="property" element={<PortalProperty />} />
        <Route path="updates" element={<PortalUpdates />} />
        <Route path="maintenance" element={<PortalMaintenance />} />
        <Route path="messages" element={<PortalMessages />} />
        <Route path="agreement" element={<PortalAgreement />} />
        <Route path="profile" element={<PortalProfile />} />
      </Route>

      {/*
        A real page rather than a redirect to the home page.

        Bouncing an unknown URL to "/" made every mistyped or dead link look
        to a crawler like a second copy of the home page — and Netlify's SPA
        fallback answers 200 for all of them, so there was nothing to tell it
        otherwise. This route is declared noindex by useRouteSeo, which is the
        signal that actually stops them being indexed.
      */}
      <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
